import { Column, ColumnDef, Row } from "@tanstack/react-table";
import {
  memo,
  Dispatch,
  SetStateAction,
  useCallback,
  useMemo,
  useState,
} from "react";
import { Button } from "../../button";
import { cn } from "../../../utils/cn";
import {
  PendingStatuses,
  PermitDetails,
  PermitTypes,
  STATUS_IDS,
  STATUS_INDICATOR_COLUMN,
  Statuses,
  TABLE_IDS,
  USER_ROLES_IDS,
} from "../../../constants/common";
import { formattedDateTimeWithoutSec } from "../../../lib/functions";
import { ArrowUpDown } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { APP_ROUTES } from "../../../routes";
import TableWrapper from "../TableWrapper";
import useAuthStore from "../../../store/Auth";
import { useTranslation } from "react-i18next";
import { ExportApprovedVisitRequestsForExcelPDFBody } from "../../../hooks/visitor/interface";
import { Toaster } from "react-hot-toast";
import CancelPermitModal from "../../modal/CancelPermitModal";

interface SearchPermitTableProps {
  allPermits: never[];
  page: number;
  pages: number;
  setPage: Dispatch<SetStateAction<number>>;
  search: string;
  setSearch: Dispatch<SetStateAction<string>>;
  count: number;
  tableId: string;
  exportBody?: ExportApprovedVisitRequestsForExcelPDFBody;
  pageSize: number;
  setPageSize: Dispatch<SetStateAction<number>>;
}

const SearchPermitTable = ({
  allPermits,
  pages,
  page,
  setPage,
  search,
  count,
  setSearch,
  tableId,
  exportBody,
  pageSize,
  setPageSize,
}: SearchPermitTableProps) => {
  const navigate = useNavigate();

  const { t, i18n } = useTranslation();

  const [isCancelPermitModalOpen, setIsCancelPermitModalOpen] =
    useState<boolean>(false);
  const [selectedDeletePermitId, setSelectedDeletePermitId] = useState(0);
  const [selectedPermitType, setSelectedPermitType] = useState(0);

  const user = useAuthStore((state) => state.user);

  const handleCancelPermit = useCallback(
    (permitId: number, permitType: number) => {
      setIsCancelPermitModalOpen(true);
      setSelectedDeletePermitId(permitId);
      setSelectedPermitType(permitType);
    },
    [],
  );

  const columns: ColumnDef<PermitDetails>[] = useMemo(
    () =>
      [
        {
          accessorKey: STATUS_INDICATOR_COLUMN.CURRENT_ROLE_STATUS,
          header: () => <></>,
          cell: ({ row }: { row: Row<PermitDetails> }) => {
            if (row.original.currentRolePermitStatusEN) {
              const isReqPendingInfo =
                row.original.currentRolePermitStatusEN ===
                Statuses.REQUEST_INFO ||
                row.original.currentRolePermitStatusEN === Statuses.PENDING;

              const isPendingWithRole =
                row.original.currentRolePermitStatusEN?.split(" - ")[0] ===
                Statuses.PENDING;

              return (
                <div
                  className={cn(
                    "text-center rounded-md mx-auto w-5 h-5",
                    row.original.currentRolePermitStatusEN === Statuses.APPROVED
                      ? "bg-green-600"
                      : isPendingWithRole
                        ? "bg-yellow-500"
                        : row.original.currentRolePermitStatusEN ===
                          Statuses.REQUEST_INFO
                          ? "bg-purple-tertiary"
                          : "bg-red-500",
                  )}
                >
                  <p className="text-white">
                    {i18n.language === "en"
                      ? !isReqPendingInfo
                        ? row.original.currentRolePermitStatusEN.split("")[0]
                        : row.original.currentRolePermitStatusEN.split("")[0]
                      : !isReqPendingInfo
                        ? row.original.currentRolePermitStatusAR.split("")[0]
                        : row.original.currentRolePermitStatusAR.split("")[0]}
                  </p>
                </div>
              );
            }
          },
        },
        {
          accessorKey: "s_PermitCode",
          header: ({ column }: { column: Column<PermitDetails> }) => (
            <Button
              className="flex w-full justify-between text-md"
              variant="ghost"
              onClick={() =>
                column.toggleSorting(column.getIsSorted() === "asc")
              }
            >
              {t("referenceNumber")}
              <ArrowUpDown className="ml-2 h-4 w-4" />
            </Button>
          ),
          cell: ({ row }: { row: Row<PermitDetails> }) => {
            return (
              <span
                onClick={() =>
                  navigate(
                    `${user?.n_RoleID === USER_ROLES_IDS.STAFF
                      ? row.original.n_PermitTypeID ===
                        PermitTypes.PC_IN_OUT_PERMIT
                        ? APP_ROUTES.UPDATE_PC_IN_OUT_PERMIT
                        : row.original.n_PermitTypeID ===
                          PermitTypes.WAREHOUSE_IN_OUT_PERMIT
                          ? APP_ROUTES.UPDATE_WAREHOUSE_IN_OUT_PERMIT
                          : row.original.n_PermitTypeID ===
                            PermitTypes.PERMIT_TO_WORK
                            ? APP_ROUTES.UPDATE_PERMIT_TO_WORK
                            : row.original.n_PermitTypeID ===
                              PermitTypes.LOST_VEHICLE_STICKER_ID_CARD
                              ? APP_ROUTES.UPDATE_LOST_VEHICLE_STICKER_ID_CARD
                              : row.original.n_PermitTypeID ===
                                PermitTypes.PARKING_STICKER_REQUEST
                                ? APP_ROUTES.UPDATE_PARKING_STICKER_REQUEST
                                : row.original.n_PermitTypeID ===
                                  PermitTypes.OFFICE_ACCESS_AUTHORIZATION
                                  ? APP_ROUTES.UPDATE_OFFICE_ACCESS_AUTHORIZATION
                                  : APP_ROUTES.UPDATE_ISSUANCE_RENEWAL_ID_FOR_STAFF_FROM_CONTRACTED_COMPANIES
                      : row.original.n_PermitTypeID ===
                        PermitTypes.PC_IN_OUT_PERMIT
                        ? APP_ROUTES.APPROVE_PC_IN_OUT_PERMIT
                        : row.original.n_PermitTypeID ===
                          PermitTypes.WAREHOUSE_IN_OUT_PERMIT
                          ? APP_ROUTES.APPROVE_WAREHOUSE_IN_OUT_PERMIT
                          : row.original.n_PermitTypeID ===
                            PermitTypes.PERMIT_TO_WORK
                            ? APP_ROUTES.APPROVE_PERMIT_TO_WORK
                            : row.original.n_PermitTypeID ===
                              PermitTypes.LOST_VEHICLE_STICKER_ID_CARD
                              ? APP_ROUTES.APPROVE_LOST_VEHICLE_STICKER_ID_CARD
                              : row.original.n_PermitTypeID ===
                                PermitTypes.PARKING_STICKER_REQUEST
                                ? APP_ROUTES.APPROVE_PARKING_STICKER_REQUEST
                                : row.original.n_PermitTypeID ===
                                  PermitTypes.OFFICE_ACCESS_AUTHORIZATION
                                  ? APP_ROUTES.APPROVE_OFFICE_ACCESS_AUTHORIZATION
                                  : APP_ROUTES.APPROVE_ISSUANCE_RENEWAL_ID_FOR_STAFF_FROM_CONTRACTED_COMPANIES
                    }?ID=${row?.original?.n_PermitID.toString()}`,
                  )
                }
                className="text-blue-800 underline cursor-pointer"
              >
                {row.original.s_PermitCode}
              </span>
            );
          },
        },
        {
          accessorKey: "type",
          header: ({ column }: { column: Column<PermitDetails> }) => (
            <Button
              className="flex w-full justify-between text-md"
              variant="ghost"
              onClick={() =>
                column.toggleSorting(column.getIsSorted() === "asc")
              }
            >
              {t("type")}
              <ArrowUpDown className="ml-2 h-4 w-4" />
            </Button>
          ),
          cell: ({ row }: { row: Row<PermitDetails> }) => {
            return row.original.n_PermitTypeID ===
              PermitTypes.PC_IN_OUT_PERMIT ? (
              <p>{t("pcInOutPermit")}</p>
            ) : row.original.n_PermitTypeID ===
              PermitTypes.WAREHOUSE_IN_OUT_PERMIT ? (
              <p>{t("warehouseInOutPermit")}</p>
            ) : row.original.n_PermitTypeID ===
              PermitTypes.PERMIT_TO_WORK ? (
              <p>{t("permitToWork")}</p>
            ) : row.original.n_PermitTypeID ===
              PermitTypes.LOST_VEHICLE_STICKER_ID_CARD ? (
              <p>{t("lostVehicleStickerIdCard")}</p>
            ) : row.original.n_PermitTypeID ===
              PermitTypes.PARKING_STICKER_REQUEST ? (
              <p>{t("parkingStickerRequest")}</p>
            ) : row.original.n_PermitTypeID ===
              PermitTypes.OFFICE_ACCESS_AUTHORIZATION ? (
              <p>{t("officeAccessAuthorization")}</p>
            ) : row.original.n_PermitTypeID ===
              PermitTypes.ISSUANCE_RENEWAL_ID ? (
              <p>{t("issuanceRenewalIdForStaff")}</p>
            ) : (
              <p>{t("otherPermit")}</p>
            );
          }
        },
        {
          accessorKey: "dt_PermitStartDate",
          header: ({ column }: { column: Column<PermitDetails> }) => (
            <Button
              className="flex w-full justify-between text-md"
              variant="ghost"
              onClick={() =>
                column.toggleSorting(column.getIsSorted() === "asc")
              }
            >
              {t("startDate")}
              <ArrowUpDown className="ml-2 h-4 w-4" />
            </Button>
          ),
          cell: ({ row }: { row: Row<PermitDetails> }) => {
            if (!row.original.dt_PermitStartDate) {
              return (
                <div className="pl-[25%]">
                  <p>-</p>
                </div>
              )
            }

            return (
              <span>
                {formattedDateTimeWithoutSec(row.original.dt_PermitStartDate)}
              </span>
            );
          },
        },
        {
          accessorKey: "dt_PermitEndDate",
          header: ({ column }: { column: Column<PermitDetails> }) => (
            <Button
              className="flex w-full justify-between text-md"
              variant="ghost"
              onClick={() =>
                column.toggleSorting(column.getIsSorted() === "asc")
              }
            >
              {t("endDate")}
              <ArrowUpDown className="ml-2 h-4 w-4" />
            </Button>
          ),
          cell: ({ row }: { row: Row<PermitDetails> }) => {
            if (!row.original.dt_PermitEndDate) {
              return (
                <div className="pl-[25%]">
                  <p>-</p>
                </div>
              )
            }

            return (
              <span>
                {formattedDateTimeWithoutSec(row.original.dt_PermitEndDate)}
              </span>
            );
          },
        },
        {
          accessorKey: "currentRolePermitStatusEN",
          header: () => (
            <Button
              className="flex w-full justify-between text-md"
              variant="ghost"
            >
              {t("currentStatus")}
            </Button>
          ),
          cell: ({ row }: { row: Row<PermitDetails> }) => {
            if (row.original.currentRolePermitStatusEN) {
              return (
                <div
                  className={cn(
                    "text-center rounded-lg py-0.5 w-[128px]",
                    row.original.currentRolePermitStatusEN === Statuses.APPROVED
                      ? "bg-green-600"
                      : row.original.currentRolePermitStatusEN ===
                        Statuses.PENDING
                        ? "bg-yellow-500"
                        : row.original.currentRolePermitStatusEN ===
                          Statuses.REQUEST_INFO
                          ? "bg-purple-tertiary"
                          : "bg-red-500",
                  )}
                >
                  <p className="text-white whitespace-normal">
                    {i18n.language === "en"
                      ? row.original.currentRolePermitStatusEN
                      : row.original.currentRolePermitStatusAR}
                  </p>
                </div>
              );
            }
          },
        },
        {
          accessorKey: "overAllPermitStatusEN",
          header: () => (
            <Button
              className="flex w-full justify-between text-md"
              variant="ghost"
            >
              {user?.n_RoleID === USER_ROLES_IDS.STAFF ? t("status") : t("finalStatus")}
            </Button>
          ),
          cell: ({ row }: { row: Row<PermitDetails> }) => {
            return (
              <div
                className={cn(
                  "text-center rounded-lg py-0.5 w-[128px]",
                  row.original.overAllPermitStatusEN === Statuses.APPROVED
                    ? "bg-green-600"
                    : row.original.overAllPermitStatusEN ===
                      PendingStatuses.PENDING_STAFF
                      ? "bg-yellow-500"
                      : row.original.overAllPermitStatusEN ===
                        PendingStatuses.PENDING_AUTHORIZED_PERSON
                        ? "bg-orange-light"
                        : row.original.overAllPermitStatusEN ===
                          PendingStatuses.PENDING_CYBER_SECURITY
                          ? "bg-purple-dark"
                          : row.original.overAllPermitStatusEN ===
                            PendingStatuses.PENDING_SECURITY
                            ? "bg-purple-orchid"
                            : row.original.overAllPermitStatusEN ===
                              PendingStatuses.PENDING_SECURITY_MANAGER
                              ? "bg-blue-primary"
                              : row.original.overAllPermitStatusEN ===
                                PendingStatuses.PENDING_DIRECT_MANAGER
                                ? "bg-purple-700"
                                : row.original.overAllPermitStatusEN ===
                                  PendingStatuses.PENDING_INVENTORY_MANAGER
                                  ? "bg-orange-600"
                                  : row.original.overAllPermitStatusEN ===
                                    Statuses.REQUEST_INFO
                                    ? "bg-purple-tertiary"
                                    : "bg-red-500",
                  user?.n_RoleID === USER_ROLES_IDS.ADMIN && "cursor-pointer",
                )}
              >
                <p className="text-sm text-white whitespace-normal">
                  {i18n.language === "en"
                    ? row.original.overAllPermitStatusEN
                    : row.original.overAllPermitStatusAR}
                </p>
              </div>
            );
          },
        },
        {
          accessorKey: "action",
          header: t("action"),
          cell: ({ row }: { row: Row<PermitDetails> }) => {
            return (
              <Button
                onClick={() =>
                  handleCancelPermit(
                    row.original.n_PermitID,
                    row.original.n_PermitTypeID,
                  )
                }
                disabled={
                  (row.original?.dt_PermitEndDate && new Date(row.original?.dt_PermitEndDate) <= new Date()) ||
                  row?.original?.n_ApprovalStatus === STATUS_IDS.APPROVED ||
                  row?.original?.n_ApprovalStatus === STATUS_IDS.CANCELED ||
                  row?.original?.n_ApprovalStatus === STATUS_IDS.REJECTED ||
                  row?.original?.n_ApprovalStatus === STATUS_IDS.EXPIRED
                }
                className="py-0.5"
              >
                {t("cancel")}
              </Button>
            );
          },
        },
      ].filter((column) => {
        let excludeKeys = [];

        if (
          user?.n_RoleID === USER_ROLES_IDS.ADMIN ||
          user?.n_RoleID === USER_ROLES_IDS.SECURITY_MANAGER ||
          user?.n_RoleID === USER_ROLES_IDS.RECEPTION
        ) {
        } else {
          excludeKeys.push(STATUS_INDICATOR_COLUMN.ADMIN_STATUS);
        }

        if (user?.n_RoleID === USER_ROLES_IDS.STAFF) {
          excludeKeys.push("currentRolePermitStatusEN", STATUS_INDICATOR_COLUMN.CURRENT_ROLE_STATUS);
        }

        if (tableId !== TABLE_IDS.SEARCH_PERMIT_REQUEST) {
          excludeKeys.push("action");
        }

        return column.accessorKey
          ? !excludeKeys.includes(column.accessorKey)
          : true;
      }),
    [t, i18n.language, navigate, user?.n_RoleID, handleCancelPermit, tableId],
  );

  return (
    <>
      <Toaster position="top-right" reverseOrder={false} />

      <TableWrapper
        data={allPermits}
        columns={columns}
        search={search}
        setSearch={setSearch}
        page={page}
        setPage={setPage}
        totalPages={pages}
        count={count}
        isSearch
        tableId={tableId}
        isHideStatusIndicator
        pageSize={pageSize}
        setPageSize={setPageSize}
      />

      <CancelPermitModal
        permitTypeId={selectedPermitType}
        selectedPermitId={selectedDeletePermitId}
        setSelectedPermitId={setSelectedDeletePermitId}
        isOpen={isCancelPermitModalOpen}
        onOpenChange={() => setIsCancelPermitModalOpen(false)}
      />
    </>
  );
};

export default memo(SearchPermitTable);
